import { mqMin, radius, space, tk, tok } from '@sunwebgroup/ui-components'
import { focusOutline, Icon, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { forwardRef } from 'react'
import { css, styled } from 'styled-components'

export const QuickSearchItem = styled.button<{ $isActive: boolean, $hasActiveSibling: boolean }>`
  cursor: pointer;
  margin-left: -1px;
  position: relative;
  height: 100%;
  flex: 1 1;
  min-width: 50px;
  border-radius: ${radius('primitives_corner_radius_s')};
  padding: ${space('primitives_space_12')} ${space('primitives_space_8')} ${space('primitives_space_12')} ${space('primitives_space_12')};
  text-align: left;
  width: 100%;
  flex-direction: row;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &::after {
    content: '';
    display: block;
    height: 1px;
    width: calc(100% - ${space('primitives_space_16')});
    background-color: ${tok('semantics_dividers_color')};
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
  }

  &:disabled {
    background-color: inherit;
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    max-width: 250px;
    &:first-of-type {
      padding-left: ${space('primitives_space_20')};
    }

    &::before {
      height: calc(100% - ${space('primitives_space_12')});
      width: 1px;
      top: 50%;
      left: 0;
      transform: translateY(-50%);
    }
  }

  ${({ $isActive, $hasActiveSibling }) => !$isActive && !$hasActiveSibling && hoverMod()}
  ${({ $isActive, $hasActiveSibling }) => !$isActive && $hasActiveSibling && activeSiblingHoverMod()}
  ${({ $isActive }) => $isActive && isActiveMod()}

  ${focusOutline()}
`
export const SelectionTextWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

export const MainLabel = styled(Typography)``

const SelectionText = styled(Typography)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const TriggerIcon = styled(Icon)`
  @media ${mqMin('primitives_breakpoint_l_min')} {
    display: none;
  }
`

const Wrapper = styled.div`
  overflow: auto;
  `

function hoverMod() {
  return css`
    @media ${mqMin('primitives_breakpoint_l_min')} {
      &:hover {
        background-color: ${tok('primitives_color_neutral_100')};//to be changed for a semantic version when available
        z-index: 1;

        &::before {
          content: initial;
        }
      }
    }

`
}

function activeSiblingHoverMod() {
  return css`
  &:hover {
    background-color: ${tk('color_semantic_bg_strong')};

    &::before {
      content: initial;
    }
  }
`
}

function isActiveMod() {
  return css`
    @media ${mqMin('primitives_breakpoint_l_min')} {
      background-color: ${tok('primitives_color_neutral_100')};
      z-index: 1;

      &::before {
        content: initial;
      }

      &:after {
        display: none;
      }
    }
`
}

export const PlaceholderText = styled(SelectionText)`
  color: ${tok('semantics_content_secondary_color')};
`

export interface ItemProps {
  title: string
  isActive: boolean
  hasActiveSibling: boolean
  placeholder?: string
  counter?: number
  additionalSelectedItems?: number
  selectionFormattedText?: string
}

export const Item = forwardRef<HTMLButtonElement, ItemProps>((props, ref) => {
  const { title, placeholder, counter, isActive, hasActiveSibling, additionalSelectedItems, selectionFormattedText } = props

  return (
    <QuickSearchItem $isActive={isActive} $hasActiveSibling={hasActiveSibling} {...props} ref={ref}>
      <Wrapper>
        <MainLabel $variant="body-s-bold-tight">
          {title}
        </MainLabel>
        <SelectionTextWrapper>
          {selectionFormattedText
            ? (
                <SelectionText $variant="body-m-regular-tight" as="p">
                  {selectionFormattedText}
                </SelectionText>
              )
            : (
                <PlaceholderText $variant="body-m-regular-tight" as="p">
                  {placeholder}
                </PlaceholderText>
              )}
          <Typography $variant="body-m-regular-tight" as="p">
            {additionalSelectedItems && additionalSelectedItems > 0 && `+${additionalSelectedItems}`}
          </Typography>

          {/* this might not be needed */}
          {counter && counter > 0
            ? (
                <Typography $variant="body-s-regular-tight" as="p">
                  {`+${counter}`}
                </Typography>
              )
            : null}
        </SelectionTextWrapper>
      </Wrapper>
      <TriggerIcon glyph="ChevronDown" size={20} />
    </QuickSearchItem>
  )
})
